import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, Box, Progress, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react'
import ReactMarkdown from 'react-markdown'
import { PageContainer } from '../components/shared/PageContainer'
import { Card } from '../components/shared/Card'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared/ResponsiveTable'
import { EmailStatusChip, EmailRecipientStatusChip } from '../components/communications/EmailStatusChip'
import { useEmailMessage, useEmailRecipients } from '../hooks/useCommunications'
import { useJobPolling } from '../hooks/useJobPolling'
import { formatShortDate } from '../lib/formatDate'
import { useReturnTo } from '../lib/returnNav'
import { proseContentCss } from '../theme/proseStyles'
import type { EmailRecipientRow } from '../types/communications'

const recipientColumns: ResponsiveColumn<EmailRecipientRow>[] = [
  { header: 'Name', mobileRole: 'title', cell: (r) => <Text>{r.name || '—'}</Text> },
  { header: 'Email', mobileRole: 'meta', cell: (r) => <Text fontSize="sm">{r.email}</Text> },
  { header: 'Status', mobileRole: 'badge', cell: (r) => <EmailRecipientStatusChip status={r.status} /> },
  {
    header: 'Failure',
    mobileRole: 'meta',
    cell: (r) => (
      <Text fontSize="sm" color={r.failureCode ? 'danger.fg' : 'fg.muted'}>{r.failureCode || '—'}</Text>
    ),
  },
]

/** Spec §8 `/communications/email/:id` -- status/counts header, job-progress while the send is
 * still in flight, the rendered message, and the audited recipient table. No "resend to failed"
 * (out of scope slice 1). */
export function EmailDetailPage() {
  const { id } = useParams<{ id: string }>()
  const back = useReturnTo({ to: '/dashboard/communications/email', label: 'Sent email' })
  const queryClient = useQueryClient()

  const { data: message, isLoading, error } = useEmailMessage(id)

  const jobId = message?.jobId ?? null
  const { data: job, isTerminal: jobTerminal } = useJobPolling(jobId)

  // Recipients are a bulk-PII audited read (backend) -- only fetch once the message itself has
  // reached a terminal status, independent of the job poll (an older message reopened later may
  // have no live jobId at all, but its status is already Sent/Failed).
  const messageTerminal = message?.status === 'Sent' || message?.status === 'Failed'
  const recipients = useEmailRecipients(id, 200, { enabled: messageTerminal })

  // Branch on `!jobTerminal` ALONE -- never `!jobTerminal || !job` (an HTTP-error/404 path
  // reports isTerminal=true with job undefined and the stricter guard would hang the progress UI
  // forever). Invalidate only inside the terminal Succeeded branch, never at the enqueue's 202
  // accept and never on a terminal Failed -- matches ListOverviewPage/ReportRunnerPage precedent.
  // Invalidating the ['communications','emails'] prefix sweeps both the list query and this
  // message's own detail/recipients queries (useSendEmail already invalidates the same prefix).
  useEffect(() => {
    if (!jobTerminal) return
    if (job?.status === 'Succeeded') {
      queryClient.invalidateQueries({ queryKey: ['communications', 'emails'] })
    }
  }, [jobTerminal, job, queryClient])

  if (isLoading) {
    return (
      <PageContainer title="Sent email" back={back}>
        <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
      </PageContainer>
    )
  }

  if (!!error || !message) {
    return (
      <PageContainer title="Sent email" back={back}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          Could not load this email.
        </Alert.Root>
      </PageContainer>
    )
  }

  // Still show the progress card if a jobId exists and hasn't resolved terminal yet, even if the
  // message row itself hasn't flipped to Sending/Sent in this fetch (e.g. right after enqueue).
  const showProgress = !!jobId && !jobTerminal

  return (
    <PageContainer
      title={message.subject}
      subtitle={`${message.sourceName} · sent ${formatShortDate(message.createdAt)}`}
      actions={<EmailStatusChip status={message.status} />}
      back={back}
    >
      <VStack align="stretch" gap={6}>
        <Card>
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
            <Box>
              <Text fontSize="xs" color="fg.muted">Recipients</Text>
              <Text fontSize="lg" fontWeight="semibold">{message.recipientCount}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted">Sent</Text>
              <Text fontSize="lg" fontWeight="semibold">{message.sentCount}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted">Suppressed</Text>
              <Text fontSize="lg" fontWeight="semibold">{message.suppressedCount}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted">Failed</Text>
              <Text fontSize="lg" fontWeight="semibold">{message.failedCount}</Text>
            </Box>
          </SimpleGrid>
          {!!message.errorMessage && (
            <Alert.Root status="error" borderRadius="md" mt={4}>
              <Alert.Indicator />
              <Alert.Content>{message.errorMessage}</Alert.Content>
            </Alert.Root>
          )}
        </Card>

        {showProgress && (
          <Card data-testid="email-send-progress">
            <Text fontWeight="semibold" mb={2}>Sending…</Text>
            <Progress.Root size="sm" value={job?.progress ?? null} colorPalette="brand" borderRadius="full">
              <Progress.Track><Progress.Range /></Progress.Track>
            </Progress.Root>
          </Card>
        )}

        <Card>
          <Text fontWeight="semibold" mb={2}>Message</Text>
          <Box css={proseContentCss}>
            <ReactMarkdown>{message.bodyMarkdown}</ReactMarkdown>
          </Box>
        </Card>

        <Box>
          <Text fontWeight="semibold" mb={2}>Recipients</Text>
          {messageTerminal ? (
            <ResponsiveTable
              columns={recipientColumns}
              rows={recipients.data ?? []}
              keyFn={(r) => r.id}
              isLoading={recipients.isLoading}
              emptyMessage="No recipients."
            />
          ) : (
            <Card textAlign="center" color="fg.muted">
              Recipients will appear once sending finishes.
            </Card>
          )}
        </Box>
      </VStack>
    </PageContainer>
  )
}
